/**
 * DIGIPIN — India Post's open-source 4×4 recursive grid addressing system.
 * Algorithm reference: github.com/INDIAPOST-gov/digipin (Apache 2.0).
 *
 * This module extends the official 10-character implementation with:
 *  - Multi-precision encoding (4-10 chars; ~16km / ~1km / ~60m / ~3.8m)
 *  - Strict typing
 *  - Bounding-box decoding (not just centre point)
 *  - O(1) reverse character lookup for decode
 */

import type { LatLng } from "./index.js";

export const DIGIPIN_GRID = [
  ["F", "C", "9", "8"],
  ["J", "3", "2", "7"],
  ["K", "4", "5", "6"],
  ["L", "M", "P", "T"],
] as const;

export const DIGIPIN_BOUNDS = {
  minLat: 2.5,
  maxLat: 38.5,
  minLon: 63.5,
  maxLon: 99.5,
} as const;

export const DIGIPIN_MIN_PRECISION = 4;
export const DIGIPIN_MAX_PRECISION = 10;

export type DigipinPrecision = 4 | 5 | 6 | 7 | 8 | 9 | 10;

export type DigipinCell = {
  readonly code: string;
  readonly center: LatLng;
  readonly bounds: {
    readonly minLat: number;
    readonly maxLat: number;
    readonly minLon: number;
    readonly maxLon: number;
  };
  readonly precision: DigipinPrecision;
};

const CHAR_TO_RC: Record<string, [number, number]> = {};
for (let r = 0; r < 4; r++) {
  for (let c = 0; c < 4; c++) {
    CHAR_TO_RC[DIGIPIN_GRID[r]![c]!] = [r, c];
  }
}

/**
 * Encode lat/lng → DIGIPIN at given precision.
 * Default precision = 10 (≈3.8m × 3.8m cell), matching the official library.
 */
export function encodeDigipin(
  lat: number,
  lng: number,
  precision: DigipinPrecision = 10,
): string {
  if (lat < DIGIPIN_BOUNDS.minLat || lat > DIGIPIN_BOUNDS.maxLat) {
    throw new RangeError(
      `Latitude ${lat} out of DIGIPIN range [${DIGIPIN_BOUNDS.minLat}, ${DIGIPIN_BOUNDS.maxLat}]`,
    );
  }
  if (lng < DIGIPIN_BOUNDS.minLon || lng > DIGIPIN_BOUNDS.maxLon) {
    throw new RangeError(
      `Longitude ${lng} out of DIGIPIN range [${DIGIPIN_BOUNDS.minLon}, ${DIGIPIN_BOUNDS.maxLon}]`,
    );
  }
  if (precision < DIGIPIN_MIN_PRECISION || precision > DIGIPIN_MAX_PRECISION) {
    throw new RangeError(
      `Precision ${precision} out of range [${DIGIPIN_MIN_PRECISION}, ${DIGIPIN_MAX_PRECISION}]`,
    );
  }

  let minLat: number = DIGIPIN_BOUNDS.minLat;
  let maxLat: number = DIGIPIN_BOUNDS.maxLat;
  let minLon: number = DIGIPIN_BOUNDS.minLon;
  let maxLon: number = DIGIPIN_BOUNDS.maxLon;

  let code = "";

  for (let level = 1; level <= precision; level++) {
    const latDiv = (maxLat - minLat) / 4;
    const lonDiv = (maxLon - minLon) / 4;

    // Reversed row logic to match official spec
    let row = 3 - Math.floor((lat - minLat) / latDiv);
    let col = Math.floor((lng - minLon) / lonDiv);
    row = Math.max(0, Math.min(row, 3));
    col = Math.max(0, Math.min(col, 3));

    code += DIGIPIN_GRID[row]![col]!;
    // Insert dash mid-stream only — never trailing
    if ((level === 3 || level === 6) && level < precision) code += "-";

    // Update bounds — preserves official ordering (uses old minLat/minLon as needed)
    maxLat = minLat + latDiv * (4 - row);
    minLat = minLat + latDiv * (3 - row);
    minLon = minLon + lonDiv * col;
    maxLon = minLon + lonDiv;
  }

  return code;
}

/**
 * Decode DIGIPIN → cell info (centre + bounds + precision).
 * Accepts any precision 4-10 chars, with or without dashes, case-insensitive.
 */
export function decodeDigipin(code: string): DigipinCell {
  const pin = code.replace(/-/g, "").toUpperCase();
  if (
    pin.length < DIGIPIN_MIN_PRECISION ||
    pin.length > DIGIPIN_MAX_PRECISION
  ) {
    throw new Error(
      `Invalid DIGIPIN: length ${pin.length} not in [${DIGIPIN_MIN_PRECISION}, ${DIGIPIN_MAX_PRECISION}]`,
    );
  }

  let minLat: number = DIGIPIN_BOUNDS.minLat;
  let maxLat: number = DIGIPIN_BOUNDS.maxLat;
  let minLon: number = DIGIPIN_BOUNDS.minLon;
  let maxLon: number = DIGIPIN_BOUNDS.maxLon;

  for (let i = 0; i < pin.length; i++) {
    const ch = pin[i]!;
    const rc = CHAR_TO_RC[ch];
    if (!rc) {
      throw new Error(`Invalid character '${ch}' at position ${i} in DIGIPIN`);
    }
    const [ri, ci] = rc;

    const latDiv = (maxLat - minLat) / 4;
    const lonDiv = (maxLon - minLon) / 4;

    const newMaxLat = maxLat - latDiv * ri;
    const newMinLat = maxLat - latDiv * (ri + 1);
    const newMinLon = minLon + lonDiv * ci;
    const newMaxLon = minLon + lonDiv * (ci + 1);

    minLat = newMinLat;
    maxLat = newMaxLat;
    minLon = newMinLon;
    maxLon = newMaxLon;
  }

  return {
    code: formatDigipin(pin),
    center: {
      lat: (minLat + maxLat) / 2,
      lng: (minLon + maxLon) / 2,
    },
    bounds: { minLat, maxLat, minLon, maxLon },
    precision: pin.length as DigipinPrecision,
  };
}

/**
 * Insert canonical dashes into a raw DIGIPIN string (after char 3 and 6).
 */
export function formatDigipin(raw: string): string {
  const clean = raw.replace(/-/g, "");
  let out = "";
  for (let i = 0; i < clean.length; i++) {
    out += clean[i];
    if ((i === 2 || i === 5) && i < clean.length - 1) out += "-";
  }
  return out;
}

/**
 * True if a string is a syntactically valid DIGIPIN (4-10 chars, all from alphabet).
 */
export function isValidDigipin(code: string): boolean {
  const pin = code.replace(/-/g, "").toUpperCase();
  if (
    pin.length < DIGIPIN_MIN_PRECISION ||
    pin.length > DIGIPIN_MAX_PRECISION
  ) {
    return false;
  }
  for (const ch of pin) {
    if (!CHAR_TO_RC[ch]) return false;
  }
  return true;
}

/**
 * Approximate cell width in metres at the given precision (latitude-dependent;
 * uses 1° ≈ 111,320m at the equator as a rough estimate).
 */
export function digipinCellWidthMetres(precision: DigipinPrecision): number {
  // Each level subdivides by 4. Total range = 36° at level 0.
  const cellWidthDegrees = 36 / Math.pow(4, precision);
  return cellWidthDegrees * 111_320;
}
